 

import { getApiErrorMessage } from "@core/utility/get-error-message";
import {
  ageRangeReportController,
  campaignReportController,
  cardStatsReportController,
  earnPointsReportController,
  earnStampReportController,
  GetResponseReportCardStat,
  GetResponseReportRank,
  rankReportController,
  redeemPointsReportController,
  redeemStampReportController,
} from "@factory/model/query-model-report";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { AxiosError } from "axios";
import { useQuery } from "react-query";

import { authHeaders } from "../authentication/auth-header";
import { useSessionContext } from "../authentication/session.context";
import { QueryKey } from "./query-key.enum";

export function useQyGetCardStatsReport(
  branchFilter?: string,
  dateFilter?: string,
  startDate?: string,
  endDate?: string,
  enabled?: boolean,
  onSuccess?:
    | ((data: GetResponseReportCardStat) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (
    branchFilter?: string,
    dateFilter?: string,
    startDate?: string,
    endDate?: string
  ) => {
    await checkSession();

    showProgress();
    const operation = await cardStatsReportController(
      branchFilter,
      dateFilter,
      startDate,
      endDate,
      authHeaders()
    );
    const response = (await operation()).data;
    return response as GetResponseReportCardStat;
  };

  return useQuery({
    enabled,
    queryKey: [QueryKey.ReportCardStatus, branchFilter, dateFilter, startDate, endDate],
    queryFn: () => apiFn(branchFilter, dateFilter, startDate, endDate),
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyGetAgeRangeReport(
  branchFilter?: string,
  dateFilter?: string,
  startDate?: string,
  endDate?: string,
  enabled?: boolean,
  onSuccess?:
    | ((data: GetResponseReportCardStat) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (
    branchFilter?: string,
    dateFilter?: string,
    startDate?: string,
    endDate?: string
  ) => {
    await checkSession();

    showProgress();
    const operation = await ageRangeReportController(
      branchFilter,
      dateFilter,
      startDate,
      endDate,
      authHeaders()
    );
    const response = (await operation()).data;
    return response as GetResponseReportCardStat;
  };

  return useQuery({
    enabled,
    queryKey: [QueryKey.ReportAgeRange, branchFilter, dateFilter, startDate, endDate],
    queryFn: () => apiFn(branchFilter, dateFilter, startDate, endDate),
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyGetRankReport(
  limit: number = 999,
  offset: number = 0,
  rankFilter?: string,
  sortBy?: string,
  sortDir?: string,
  dateFilter?: string,
  startDate?: string,
  endDate?: string,
  enabled?: boolean,
  onSuccess?:
    | ((data: GetResponseReportRank) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (
    limit?: number,
    offset?: number,
    rankFilter?: string,
    sortBy?: string,
    sortDir?: string,
    dateFilter?: string,
    startDate?: string,
    endDate?: string
  ) => {
    await checkSession();

    showProgress();
    const operation = await rankReportController(
      limit,
      offset,
      rankFilter,
      sortBy,
      sortDir,
      dateFilter,
      startDate,
      endDate,
      authHeaders()
    );
    const response = (await operation()).data;
    return response["data"] as GetResponseReportRank;
  };

  return useQuery({
    enabled,
    queryKey: [
      QueryKey.ReportRank,
      limit,
      offset,
      rankFilter,
      sortBy,
      sortDir,
      dateFilter,
      startDate,
      endDate,
    ],
    queryFn: () => apiFn(limit, offset, rankFilter, sortBy, sortDir, dateFilter, startDate, endDate),
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyGetCampaignReport(
  branchFilter?: string,
  dateFilter?: string,
  startDate?: string,
  endDate?: string,
  enabled?: boolean,
  onSuccess?:
    | ((data: GetResponseReportCardStat) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (
    branchFilter?: string,
    dateFilter?: string,
    startDate?: string,
    endDate?: string
  ) => {
    await checkSession();

    showProgress();
    const operation = await campaignReportController(
      branchFilter,
      dateFilter,
      startDate,
      endDate,
      authHeaders()
    );
    const response = (await operation()).data;
    return response["data"] as GetResponseReportCardStat;
  };

  return useQuery({
    enabled,
    queryKey: [QueryKey.ReportCampaign, branchFilter, dateFilter, startDate, endDate],
    queryFn: () => apiFn(branchFilter, dateFilter, startDate, endDate),
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyGetEarnPointsReport(
  branchFilter?: string,
  dateFilter?: string,
  startDate?: string,
  endDate?: string,
  enabled?: boolean,
  onSuccess?:
    | ((data: GetResponseReportCardStat) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (
    branchFilter?: string,
    dateFilter?: string,
    startDate?: string,
    endDate?: string
  ) => {
    await checkSession();

    showProgress();
    const operation = await earnPointsReportController(
      branchFilter,
      dateFilter,
      startDate,
      endDate,
      authHeaders()
    );
    const response = (await operation()).data;
    return response["data"] as GetResponseReportCardStat;
  };

  return useQuery({
    enabled,
    queryKey: [QueryKey.ReportEarnPoints, branchFilter, dateFilter, startDate, endDate],
    queryFn: () => apiFn(branchFilter, dateFilter, startDate, endDate),
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyGetEarnStampReport(
  branchFilter?: string,
  dateFilter?: string,
  startDate?: string,
  endDate?: string,
  enabled?: boolean,
  onSuccess?:
    | ((data: GetResponseReportCardStat) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (
    branchFilter?: string,
    dateFilter?: string,
    startDate?: string,
    endDate?: string
  ) => {
    await checkSession();

    showProgress();
    const operation = await earnStampReportController(
      branchFilter,
      dateFilter,
      startDate,
      endDate,
      authHeaders()
    );
    const response = (await operation()).data;
    return response["data"] as GetResponseReportCardStat;
  };

  return useQuery({
    enabled,
    queryKey: [QueryKey.ReportEarnStamp, branchFilter, dateFilter, startDate, endDate],
    queryFn: () => apiFn(branchFilter, dateFilter, startDate, endDate),
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyGetRedeemPointsReport(
  branchFilter?: string,
  dateFilter?: string,
  startDate?: string,
  endDate?: string,
  enabled?: boolean,
  onSuccess?:
    | ((data: GetResponseReportCardStat) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (
    branchFilter?: string,
    dateFilter?: string,
    startDate?: string,
    endDate?: string
  ) => {
    await checkSession();

    showProgress();
    const operation = await redeemPointsReportController(
      branchFilter,
      dateFilter,
      startDate,
      endDate,
      authHeaders()
    );
    const response = (await operation()).data;
    return response["data"] as GetResponseReportCardStat;
  };

  return useQuery({
    enabled,
    queryKey: [QueryKey.ReportRedeemPoints, branchFilter, dateFilter, startDate, endDate],
    queryFn: () => apiFn(branchFilter, dateFilter, startDate, endDate),
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyGetRedeemStampReport(
  branchFilter?: string,
  dateFilter?: string,
  startDate?: string,
  endDate?: string,
  enabled?: boolean,
  onSuccess?:
    | ((data: GetResponseReportCardStat) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (
    branchFilter?: string,
    dateFilter?: string,
    startDate?: string,
    endDate?: string
  ) => {
    await checkSession();

    showProgress();
    const operation = await redeemStampReportController(
      branchFilter,
      dateFilter,
      startDate,
      endDate,
      authHeaders()
    );
    const response = (await operation()).data;
    return response["data"] as GetResponseReportCardStat;
  };

  return useQuery({
    enabled,
    queryKey: [QueryKey.ReportRedeemStamp, branchFilter, dateFilter, startDate, endDate],
    queryFn: () => apiFn(branchFilter, dateFilter, startDate, endDate),
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}
